import { useEffect, useState, useMemo } from "react";
import { Timer, AlertTriangle } from "lucide-react";

// SLA minutes by attendance type. Waiting customers give us 90 mins before
// the customer starts fidgeting in the showroom; express is a tighter 60-min
// promise. When a job is BOTH waiting + express we honour the shorter promise.
const SLA_MINUTES = {
  waiting: 90,
  express: 60,
  both: 60,
};

function slaMinutesFor(job) {
  const w = !!job?.customer_waiting;
  const e = !!job?.express_service;
  if (w && e) return SLA_MINUTES.both;
  if (w) return SLA_MINUTES.waiting;
  if (e) return SLA_MINUTES.express;
  return null;
}

function fmtRemaining(mins) {
  const abs = Math.abs(mins);
  if (abs < 60) return `${abs}m`;
  const h = Math.floor(abs / 60);
  const m = abs % 60;
  return m === 0 ? `${h}h` : `${h}h ${m}m`;
}

/**
 * Live-ticking SLA countdown badge for waiting-customer + express-service jobs.
 * - Green while >25% of the SLA window remains
 * - Amber when ≤25% remains
 * - Red once the deadline has passed (shows "Xm overdue")
 * - Returns null if the job doesn't qualify (not checked in, or already ready)
 *
 * The badge updates every 15s — enough for a service department, cheap for CPU.
 */
export default function SlaCountdown({ job, size = "sm", className = "" }) {
  const slaMins = useMemo(() => slaMinutesFor(job), [job]);
  const checkedInAt = job?.checked_in_at;
  const readyAt = job?.ready_for_collection_at;
  const completedAt = job?.completed_at;

  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!slaMins || !checkedInAt || readyAt || completedAt) return;
    const t = setInterval(() => setNow(Date.now()), 15_000);
    return () => clearInterval(t);
  }, [slaMins, checkedInAt, readyAt, completedAt]);

  // Bail out silently for jobs that don't have an active SLA promise
  if (!slaMins || !checkedInAt) return null;
  if (readyAt || completedAt) return null;

  const startMs = new Date(checkedInAt).getTime();
  if (Number.isNaN(startMs)) return null;
  const deadlineMs = startMs + slaMins * 60_000;
  const remainingMins = Math.round((deadlineMs - now) / 60_000);
  const overdue = remainingMins < 0;
  // "Danger zone" — <25% of the SLA window left
  const amber = !overdue && remainingMins <= Math.max(5, Math.floor(slaMins * 0.25));

  const colour = overdue
    ? "bg-destructive/15 text-destructive border-destructive/40 animate-pulse"
    : amber
      ? "bg-warning/15 text-warning border-warning/40"
      : "bg-success/15 text-success border-success/30";

  const label = overdue
    ? `${fmtRemaining(remainingMins)} overdue`
    : `${fmtRemaining(remainingMins)} left`;

  const reasonChips = [];
  if (job.customer_waiting) reasonChips.push("Waiting");
  if (job.express_service) reasonChips.push("Express");

  const sizeCls = size === "lg"
    ? "px-2.5 py-1 text-xs gap-1.5"
    : "px-1.5 py-0.5 text-[10px] gap-1";

  return (
    <span
      className={`inline-flex items-center rounded border font-semibold ${colour} ${sizeCls} ${className}`}
      title={`${reasonChips.join(" + ")} · SLA ${slaMins} min from check-in`}
      data-testid={`sla-countdown-${job.id}`}
    >
      {overdue ? <AlertTriangle className="w-3 h-3" /> : <Timer className="w-3 h-3" />}
      {label}
    </span>
  );
}
